import { Button, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import * as React from 'react';

interface Props {
  /** The value to copy to the clipboard */
  value: string;
  /** The string describing what will happen if the user clicks */
  label: string;
  /** The type of copy button to render; defaults to button */
  variant?: 'button' | 'icon' | 'icon-small';
  /** Additional class names to apply to the button */
  className?: string;
  /** The function to call when the button is clicked */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const COPIED_HOLD = 1000;

export const CopyTextButton: React.FC<Props> = (props) => {
  const { value, label, variant, className, onClick } = props;
  const timer = React.useRef(0);
  const [copied, setCopied] = React.useState(false);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    timer.current = window.setTimeout(() => setCopied(false), COPIED_HOLD);
    onClick?.(e);
  };

  React.useEffect(() => {
    return () => {
      window.clearTimeout(timer.current);
    };
  }, []);

  switch (variant) {
    case 'icon':
      return (
        <Tooltip title={copied ? 'Copied' : label}>
          <IconButton onClick={handleClick}>
            <ContentCopyIcon
              style={{ fontSize: '17px' }}
              className={`cursor-pointer text-gray-600 ${className}}`}
            />
          </IconButton>
        </Tooltip>
      );
    case 'icon-small':
      return (
        <Tooltip title={copied ? 'Copied' : label}>
          <IconButton onClick={handleClick} className="py-0 px-1">
            <ContentCopyIcon
              style={{ fontSize: '12px' }}
              className={`cursor-pointer text-gray-600 ${className}}`}
            />
          </IconButton>
        </Tooltip>
      );
    case 'button':
    default:
      return (
        <Button
          onClick={handleClick}
          size="small"
          variant="contained"
          color="info"
          className={`space-x-1 py-0 ${className}`}
          sx={{ minWidth: 'unset' }}
        >
          <ContentCopyIcon style={{ fontSize: '12px' }} />
          <h5>{copied ? 'Copied' : label}</h5>
        </Button>
      );
  }
};
CopyTextButton.displayName = 'CopyTextButton';
